import { ApiMediaResponse } from '@client/new/api/types';

// Held opaquely by the form. Loose shape: every field optional and nullable so
// upstream sources (CollectionMedia, FullMedia from upload) flow in without
// per-field normalization. Backend accepts the looser shape.
export type FormMedia =
  | {
      [K in keyof ApiMediaResponse]?: ApiMediaResponse[K] | null;
    }
  | null;

export type FormReview = {
  name: string;
  text: string;
  media: FormMedia;
};

export type FormFaq = {
  question: string;
  answer: string;
};

export type ProgramFormValues = {
  name: string;
  goal: string;
  description: string;
  mediaCover: FormMedia;
  mediaIntro: FormMedia;
  color: string;
  tags: string[];
  targetArea: string[];
  requiredEquipment: string[];
  location: string;
  difficulty: string;
  contentStructure: StructureChoice;
  publishingBehaviour: string;
  workoutLimit: string;
  durationLength: string;
  durationUnit: string;
  daysPerWeek: string;
  sessionDuration: string;
  reviewsEnabled: boolean;
  reviews: FormReview[];
  faqsEnabled: boolean;
  faqs: FormFaq[];
  inAppPurchaseAllowed: boolean;
  // Program-level swaps toggle, loaded from the engine after the form mounts.
  // Stays undefined (and is never sent) while loading, when the engine is
  // unavailable, or when the curation flag is off.
  swapsEnabled?: boolean;
};

export const REVIEWS_MAX_ITEMS = 11;

export const emptyReview = (): FormReview => ({
  name: '',
  text: '',
  media: null,
});

export const emptyFaq = (): FormFaq => ({
  question: '',
  answer: '',
});

export type StructureChoice = 'sequential' | 'dayweek' | 'category';

export const STRUCTURE_OPTIONS: ReadonlyArray<{
  value: StructureChoice;
  label: string;
}> = [
  { value: 'sequential', label: 'Series' },
  { value: 'dayweek', label: 'Week/Day' },
  { value: 'category', label: 'Category' },
];

export const PUBLISHING_BEHAVIOUR_OPTIONS: ReadonlyArray<{
  value: string;
  label: string;
}> = [
  { value: 'top', label: 'Newest at the top' },
  { value: 'bottom', label: 'Newest at the bottom' },
];

export const DEFAULT_CONTENT_STRUCTURE = 'sequential';
export const DEFAULT_PUBLISHING_BEHAVIOUR_PROGRAM = 'bottom';
export const DEFAULT_PUBLISHING_BEHAVIOUR_CATEGORY = 'top';

export const DURATION_UNIT_OPTIONS: ReadonlyArray<{
  value: string;
  label: string;
}> = [
  { value: 'days', label: 'Days' },
  { value: 'weeks', label: 'Weeks' },
  { value: 'months', label: 'Months' },
];

export const SESSION_DURATION_OPTIONS: ReadonlyArray<{
  value: string;
  label: string;
}> = [
  { value: '<30', label: '<30 min' },
  { value: '30-45', label: '30-45 min' },
  { value: '45-60', label: '45-60 min' },
  { value: '60-90', label: '60-90 min' },
  { value: '>90', label: '>90 min' },
];

export const DEFAULT_DURATION_LENGTH = '4';
export const DEFAULT_DURATION_UNIT = 'weeks';

export const DEFAULT_COLOR = '6367ef';

export const CATEGORY_OPTIONS: string[] = [
  'Yoga',
  'Pilates',
  'Barre',
  'HIIT',
  'Cardio',
  'Cycling',
  'Crossfit',
  'Running',
  'Meditation',
  'Boxing',
  'Prenatal',
  'Postnatal',
  'Bodyweight',
  'Bodybuilding',
  'Calisthenics',
  'Tabata',
  'Sports',
  'Bikini Body',
  'Dance',
  'Warm Up',
  'Stretching',
  'Kettlebell',
  'Functional',
  'Weight lifting',
];

export const TARGET_AREA_OPTIONS: string[] = [
  'Arms',
  'Back',
  'Biceps',
  'Chest',
  'Core/Abs',
  'Full Body',
  'Glutes',
  'Legs',
  'Lower Body',
  'Shoulders',
  'Triceps',
  'Upper Body',
  'Other',
];

export const LOCATION_OPTIONS: string[] = ['Home', 'Gym', 'Outdoor'];

export const DIFFICULTY_OPTIONS: string[] = [
  'All Levels',
  'Beginner',
  'Intermediate',
  'Advanced',
];

export const CATEGORY_LIMIT = 3;
export const TARGET_AREA_LIMIT = 2;
export const EQUIPMENT_MAX_ITEMS = 7;
export const EQUIPMENT_MAX_LENGTH = 30;

export const PROGRAM_GOAL_OPTIONS: string[] = [
  'Weight Loss',
  'Gain Muscle',
  'Lean Muscle',
  'Strength',
  'Conditioning',
  'Toning',
  'Shredding',
  'Recovery',
  'Athletic Performance',
  'Skills Training',
  'Flexibility',
  'Mobility',
  'Endurance',
  'Mindfulness',
  'Progression',
];
